import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { addWeeks, format, parseISO } from 'date-fns';
import { Check } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useMembers } from '../../hooks/useMembers';
import { useEvents } from '../../hooks/useEvents';
import { supabase } from '../../lib/supabase';
import { AppShell } from '../../components/layout/AppShell';
import { PageHeader } from '../../components/layout/PageHeader';
import { Card, CardHeader } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input, Select, Textarea } from '../../components/ui/Input';
import { EventType } from '../../types';
import { getNextSabbath } from '../../lib/dates';

/** Generate the next N Saturdays starting from the next upcoming one */
function getUpcomingSabbaths(count = 8): Date[] {
  const first = getNextSabbath();
  return Array.from({ length: count }, (_, i) => addWeeks(first, i));
}

export function CreateEventPage() {
  const { user } = useAuth();
  const { members } = useMembers();
  const { events } = useEvents();
  const navigate = useNavigate();

  const sabbaths = getUpcomingSabbaths(8);
  const scheduledDates = new Set(events.map((e) => e.date));

  // Pre-select the first Saturday that isn't already scheduled
  const firstAvailable = sabbaths.find(
    (s) => !scheduledDates.has(format(s, 'yyyy-MM-dd'))
  );
  const defaultDate = format(firstAvailable ?? sabbaths[0], 'yyyy-MM-dd');

  const [selectedDate, setSelectedDate] = useState<string>(defaultDate);
  const [title, setTitle] = useState('Sabbath Lunch');
  const [time, setTime] = useState('14:00');
  const [type, setType] = useState<EventType>('sabbath_lunch');
  const [hostId, setHostId] = useState('');
  const [description, setDescription] = useState('');
  const [locationOverride, setLocationOverride] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setSaving(true);
    setError('');

    const { data, error: err } = await supabase
      .from('events')
      .insert({
        title,
        date: selectedDate,
        time: time || null,
        type,
        host_user_id: hostId || user.id, // default to creator as host
        description: description || null,
        location_override: locationOverride || null,
        created_by: user.id,
      })
      .select()
      .single();

    setSaving(false);

    if (err) {
      setError(err.message);
    } else if (data) {
      navigate(`/events/${data.id}`);
    }
  };

  const displayTime = (() => {
    try {
      return format(new Date(`1970-01-01T${time}`), 'h:mm a');
    } catch {
      return time;
    }
  })();

  return (
    <AppShell>
      <PageHeader title="Schedule a Lunch" back />

      <div className="px-4 pt-3 pb-6 space-y-4">

        {/* Saturday picker */}
        <Card>
          <CardHeader
            title="Choose a Saturday"
            subtitle="Tap a date to select it"
          />

          <div className="space-y-2">
            {sabbaths.map((sat) => {
              const dateStr = format(sat, 'yyyy-MM-dd');
              const isSelected = dateStr === selectedDate;
              const isScheduled = scheduledDates.has(dateStr);
              const showYear = sat.getFullYear() !== new Date().getFullYear();

              return (
                <button
                  key={dateStr}
                  type="button"
                  onClick={() => { if (!isScheduled) setSelectedDate(dateStr); }}
                  disabled={isScheduled}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-2xl border transition-all text-left ${
                    isSelected
                      ? 'bg-warm-500 border-warm-500 shadow-sm'
                      : isScheduled
                      ? 'bg-gray-50 dark:bg-gray-800/50 border-gray-100 dark:border-gray-800 opacity-50 cursor-not-allowed'
                      : 'bg-white dark:bg-gray-900 border-warm-100 dark:border-gray-700 hover:border-warm-300 dark:hover:border-warm-700 hover:bg-warm-50 dark:hover:bg-gray-800 active:scale-[0.98]'
                  }`}
                >
                  <div>
                    <p className={`text-sm font-semibold ${isSelected ? 'text-white' : 'text-gray-800 dark:text-gray-100'}`}>
                      {format(sat, 'EEEE, MMMM d')}
                      {showYear && (
                        <span className={`font-normal ${isSelected ? 'text-warm-100' : 'text-gray-400 dark:text-gray-500'}`}>
                          {' '}{sat.getFullYear()}
                        </span>
                      )}
                    </p>
                    {isScheduled && (
                      <p className="text-xs text-warm-500 dark:text-warm-400 mt-0.5">
                        Already scheduled
                      </p>
                    )}
                  </div>

                  {isSelected && (
                    <div className="w-6 h-6 rounded-full bg-white/25 flex items-center justify-center flex-shrink-0">
                      <Check size={14} className="text-white" />
                    </div>
                  )}
                  {!isSelected && !isScheduled && (
                    <div className="w-5 h-5 rounded-full border-2 border-gray-200 dark:border-gray-600 flex-shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        </Card>

        {/* Event details */}
        <Card>
          <CardHeader title="Details" />

          <form onSubmit={handleSubmit} className="space-y-3">
            <Input
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Sabbath Lunch"
              required
            />

            <Input
              label="Time"
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
            />

            <Select
              label="Event Type"
              value={type}
              onChange={(e) => setType(e.target.value as EventType)}
            >
              <option value="sabbath_lunch">Sabbath Lunch</option>
              <option value="special_event">Special Event</option>
            </Select>

            <Select
              label="Host (optional)"
              value={hostId}
              onChange={(e) => setHostId(e.target.value)}
            >
              <option value="">No host assigned yet</option>
              {members.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.full_name}
                  {m.spouse_name ? ` & ${m.spouse_name.split(' ')[0]}` : ''}
                </option>
              ))}
            </Select>

            <Textarea
              label="Description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Any details or notes for this gathering…"
            />

            <Input
              label="Location Override (optional)"
              value={locationOverride}
              onChange={(e) => setLocationOverride(e.target.value)}
              placeholder="Only if different from the host's address"
            />

            {error && (
              <div className="rounded-xl bg-red-50 dark:bg-red-950 border border-red-100 dark:border-red-900 px-3 py-2.5 text-sm text-red-600 dark:text-red-400">
                {error}
              </div>
            )}

            {/* Confirmation summary */}
            <div className="rounded-xl bg-warm-50 dark:bg-warm-950 border border-warm-100 dark:border-warm-900 px-3 py-2.5 text-sm text-warm-700 dark:text-warm-300">
              Scheduling <span className="font-semibold">{title}</span> for{' '}
              <span className="font-semibold">
                {format(parseISO(selectedDate), 'MMMM d')}
              </span>{' '}
              at <span className="font-semibold">{displayTime}</span>
            </div>

            <Button type="submit" fullWidth size="lg" loading={saving}>
              Create Event
            </Button>
          </form>
        </Card>

      </div>
    </AppShell>
  );
}
